import { c as _c } from "react/compiler-runtime";
import { HotelInfo } from "@website/accommodation-ui/hotel-card-new";
import { getOrigin } from "@website/utils/channel/get-origin";
import { getImage } from "@website/utils/get-image";
type VillaSchemaData = {
  description?: string;
  occupancy?: number;
};
type SchemaVillaInfo = HotelInfo & {
  schemaData?: VillaSchemaData;
};
type SchemaVillaListPagePropertiesType = {
  villas: Array<SchemaVillaInfo>;
  path: string;
  locale?: string;
  name?: string;
  description?: string;
  itemListName?: string;
  locationTitle?: string;
  dateFrom?: unknown;
  dateTo?: unknown;
  roomCapacity?: unknown;
};
const stringifySchema = (schema: unknown) => JSON.stringify(schema).replaceAll("<", String.raw`\u003c`);
const VILLA_BASE_PATH = "/villa";
const TOMAN_TO_RIAL_RATE = 10;
const getText = (value?: unknown) => typeof value === "string" ? value.trim() : "";
const getPositiveNumber = (value?: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : undefined;
};
const getAbsoluteVillaUrl = (path: string) => `${getOrigin()}${VILLA_BASE_PATH}${path}`;
const getVillaRoomUrl = (villa: SchemaVillaInfo) => getAbsoluteVillaUrl(`/room/${villa.id}`);
const getVillaImages = (villa: SchemaVillaInfo) => [...(villa.gallery?.slice(0, 5).map(image => image?.media) ?? []), villa.thumbnail?.url].filter((image): image is string => Boolean(getText(image))).map(src => getImage({
  width: 350,
  height: 350,
  src
})).filter((image): image is string => Boolean(getText(image))).filter((image, index, images) => images.indexOf(image) === index);
const getVillaAddress = (villa: SchemaVillaInfo) => {
  const streetAddress = getText(villa.hotelDetail?.address);
  const addressLocality = getText(villa.hotelDetail?.cityTitle);
  if (!streetAddress && !addressLocality) return undefined;
  return {
    "@type": "PostalAddress",
    ...(streetAddress ? {
      streetAddress
    } : {}),
    ...(addressLocality ? {
      addressLocality
    } : {})
  };
};
const getVillaGeo = (villa: SchemaVillaInfo) => {
  const latitude = Number(villa.location?.lat);
  const longitude = Number(villa.location?.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined;
  }
  return {
    "@type": "GeoCoordinates",
    latitude,
    longitude
  };
};
const getVillaContainsPlace = (villa: SchemaVillaInfo) => {
  const occupancy = getPositiveNumber(villa.schemaData?.occupancy);
  if (!occupancy) return undefined;
  return {
    "@type": "Accommodation",
    occupancy: {
      "@type": "QuantitativeValue",
      value: occupancy
    }
  };
};
const getVillaAggregateRating = (villa: SchemaVillaInfo) => {
  const ratingValue = getPositiveNumber(villa.hotelDetail?.rating);
  const ratingCount = getPositiveNumber(villa.hotelDetail?.reviewCount);
  if (!ratingValue || !ratingCount) return undefined;
  return {
    "@type": "AggregateRating",
    ratingValue,
    ratingCount,
    reviewCount: ratingCount
  };
};
const getVillaPrice = ({
  villa,
  dateFrom,
  dateTo,
  roomCapacity
}: Pick<SchemaVillaListPagePropertiesType, "dateFrom" | "dateTo" | "roomCapacity"> & {
  villa: SchemaVillaInfo;
}) => {
  const {
    price,
    priceOff,
    basePriceOff
  } = villa.pricing;
  const discountedPrice = getPositiveNumber(priceOff) && price > Number(priceOff) ? Number(priceOff) : getPositiveNumber(price);
  const hasDateAndPassengers = Boolean(dateFrom && dateTo && getPositiveNumber(roomCapacity));
  return hasDateAndPassengers ? discountedPrice : getPositiveNumber(basePriceOff) ?? discountedPrice;
};
const getVillaOffer = ({
  villa,
  url,
  dateFrom,
  dateTo,
  roomCapacity
}: Pick<SchemaVillaListPagePropertiesType, "dateFrom" | "dateTo" | "roomCapacity"> & {
  villa: SchemaVillaInfo;
  url: string;
}) => {
  const price = getVillaPrice({
    villa,
    dateFrom,
    dateTo,
    roomCapacity
  });
  if (!price) return undefined;
  return {
    "@type": "Offer",
    price: price * TOMAN_TO_RIAL_RATE,
    priceCurrency: "IRR",
    availability: "https://schema.org/InStock",
    url
  };
};
const getDefaultName = (locationTitle?: string) => locationTitle ? `اجاره ویلا در ${locationTitle}` : "اجاره ویلا و اقامتگاه";
const getDefaultDescription = (locationTitle?: string) => locationTitle ? `رزرو و اجاره ویلا در ${locationTitle} با بهترین قیمت در اسنپ تریپ.` : "رزرو و اجاره ویلا و اقامتگاه با بهترین قیمت در اسنپ تریپ.";
export const SchemaVillaListPage = t0 => {
  const $ = _c(18);
  const {
    villas,
    path,
    locale,
    name,
    description,
    itemListName,
    locationTitle,
    dateFrom,
    dateTo,
    roomCapacity
  } = t0;
  let t1;
  let t2;
  if ($[0] !== dateFrom || $[1] !== dateTo || $[2] !== description || $[3] !== itemListName || $[4] !== locale || $[5] !== locationTitle || $[6] !== name || $[7] !== path || $[8] !== roomCapacity || $[9] !== villas) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const pageUrl = getAbsoluteVillaUrl(path);
      const pageName = getText(name) || getDefaultName(locationTitle);
      const pageDescription = getText(description) || getDefaultDescription(locationTitle);
      const resolvedItemListName = getText(itemListName) || pageName;
      let t3;
      if ($[12] !== dateFrom || $[13] !== dateTo || $[14] !== roomCapacity) {
        t3 = (villa_0, index) => {
          const url = getVillaRoomUrl(villa_0);
          const images = getVillaImages(villa_0);
          const description_0 = getText(villa_0.schemaData?.description);
          const address = getVillaAddress(villa_0);
          const geo = getVillaGeo(villa_0);
          const containsPlace = getVillaContainsPlace(villa_0);
          const aggregateRating = getVillaAggregateRating(villa_0);
          const offers = getVillaOffer({
            villa: villa_0,
            url,
            dateFrom,
            dateTo,
            roomCapacity
          });
          return {
            "@type": "ListItem",
            position: index + 1,
            url,
            item: {
              "@type": "VacationRental",
              identifier: `${villa_0.id}`,
              name: villa_0.hotelDetail.hotelTitle,
              ...(description_0 ? {
                description: description_0
              } : {}),
              ...(images.length > 0 ? {
                image: images
              } : {}),
              ...(address ? {
                address
              } : {}),
              ...(geo ? {
                geo
              } : {}),
              ...(containsPlace ? {
                containsPlace
              } : {}),
              ...(aggregateRating ? {
                aggregateRating
              } : {}),
              ...(offers ? {
                offers
              } : {})
            }
          };
        };
        $[12] = dateFrom;
        $[13] = dateTo;
        $[14] = roomCapacity;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      const itemListElement = villas.filter(_temp).map(t3);
      if (itemListElement.length === 0) {
        t2 = null;
        break bb0;
      }
      t1 = stringifySchema({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${pageUrl}#collectionpage`,
        url: pageUrl,
        name: pageName,
        description: pageDescription,
        inLanguage: locale || "fa",
        mainEntity: {
          "@type": "ItemList",
          name: resolvedItemListName,
          numberOfItems: itemListElement.length,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          itemListElement
        }
      });
    }
    $[0] = dateFrom;
    $[1] = dateTo;
    $[2] = description;
    $[3] = itemListName;
    $[4] = locale;
    $[5] = locationTitle;
    $[6] = name;
    $[7] = path;
    $[8] = roomCapacity;
    $[9] = villas;
    $[10] = t1;
    $[11] = t2;
  } else {
    t1 = $[10];
    t2 = $[11];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[16] !== t1) {
    t3 = <script dangerouslySetInnerHTML={{
      __html: t1
    }} suppressHydrationWarning={true} type="application/ld+json" />;
    $[16] = t1;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
function _temp(villa) {
  return villa.hotelDetail?.isAvailable && Boolean(getText(villa.hotelDetail?.hotelTitle));
}